import { useEffect, useState } from 'react';
import { IAchievement } from '@ifit/gateway-achievement-service';
import cn from 'classnames';
import { useMilestone } from '../../../state-management/server-state/use-milestone';
import './index.scss';

interface IProps {
  workoutCount: number;
  year: number;
}

export default function TrophyCase({ workoutCount, year }: IProps): JSX.Element {
  const { data, isLoading, isSuccess } = useMilestone(year);
  const [badgesToShow, setBadgesToShow] = useState('');

  useEffect(() => {
    function handleResize(): void {
      const getBadgesToShow = getComputedStyle(document.documentElement).getPropertyValue('--badges-to-show');

      setBadgesToShow(getBadgesToShow);
    }

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (isLoading && !isSuccess) {
    return null;
  }

  function findLastCompleted(data: IAchievement[]): number {
    return data.map(({ milestoneCount }) => workoutCount >= milestoneCount).lastIndexOf(true);
  }

  const lastCompletedMilestoneIndex = findLastCompleted(data);

  function filterBadgesToShow(
    lastCompletedIndex: number = lastCompletedMilestoneIndex,
    milestones = data,
    badgesToDisplay: number = Number.parseInt(badgesToShow, 10)
  ): IAchievement[] {
    // No workouts completed yet.
    if (lastCompletedIndex === -1) {
      return milestones.slice(0, badgesToDisplay);
    }

    // Return the last completed milestone first and fill the rest with the badgesToDisplay.
    if (lastCompletedIndex + badgesToDisplay < milestones.length) {
      return milestones.slice(lastCompletedIndex, lastCompletedIndex + badgesToDisplay);
    }

    // Otherwise return the end of the array with the badgesToDisplay.
    return milestones.slice(-badgesToDisplay);
  }

  const filteredLastCompleted = findLastCompleted(filterBadgesToShow());

  return (
    <ul className="achievements-trophy-case" data-testid="achievements-trophy-case">
      {filterBadgesToShow().map(({ badgeImage, id, milestoneCount }, index) => {
        return (
          <li
            key={id}
            className={cn('achievements-trophy', {
              completed: workoutCount >= milestoneCount,
              highlighted: filteredLastCompleted === index,
            })}
          >
            <img src={badgeImage} className="achievements-badge" />
          </li>
        );
      })}
    </ul>
  );
}
